// Axios client — Rialto platform auth pattern (cookie-based sealed_session).
//
// Replaces the prior Bearer-token interceptor with `withCredentials: true`
// so the browser auto-attaches the httpOnly `wos_session` cookie that the
// server sets at /auth/callback. No bearer tokens are read or stored
// client-side; the server fully owns the auth state.
//
// On 401:
//   - Outside the auth flow itself, navigate to /auth/login (SERVER endpoint —
//     handles silent SSO via prompt=none + interactive fallback).
//   - Return a never-resolving promise so the React caller stays in its
//     loading state instead of catching ApiError and flashing SignIn /
//     login UI before the navigation completes.
//
// Same-origin transport: BASE_URL defaults to '' (relative paths). In prod
// the SPA + API are served from the same FastAPI process behind one ingress
// hostname; no cross-origin. In local dev (Vite at :5173), set
// VITE_API_BASE_URL=http://localhost:8000 in .env.local — the dev API
// allows that origin via CORS.

import axios, { type AxiosError } from 'axios'
import type { ApiError } from '@/types/domain'

// Extend AxiosRequestConfig so callers can opt out of the automatic
// 403 → /unauthorized hard-redirect on a per-request basis.
// Usage: pass { skipGlobal403: true } in the axios config (or use getFallible<T>).
declare module 'axios' {
  interface AxiosRequestConfig {
    skipGlobal403?: boolean
  }
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? ''

export const apiClient = axios.create({
  baseURL: `${BASE_URL}/api/v1`,
  timeout: 30_000,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
})

// ─── Response interceptor: cookie-auth handling + error normalization ────────
apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const apiError: ApiError = {
      status:  error.response?.status ?? 0,
      message: 'An unexpected error occurred.',
      detail:  undefined,
    }

    if (error.response) {
      const data = error.response.data as Record<string, unknown>
      apiError.message = (data?.message as string) || (data?.detail as string) || apiError.message
      apiError.detail  = data?.detail as Record<string, unknown>

      if (error.response.status === 401) {
        // Server has evicted (or never had) our wos_session cookie. Before
        // bouncing through the full WorkOS flow, check whether LOCAL_AUTH_BYPASS
        // is active (local dev only). If it is, /auth/login sets a bypass cookie
        // and redirects straight back to / — no WorkOS round-trip, no password
        // prompt, silent auto-login on every container restart.
        //
        // Skip the redirect when we're already inside the auth flow itself
        // — avoids redirect loops if /auth/login or /auth/callback itself 401s.
        const path = window.location.pathname
        if (!path.startsWith('/auth/')) {
          // Preserve the deep link the user landed on (e.g. an email link to
          // /pipeline/<id>) so the server bounces them back here after SSO. The
          // server carries this through the OAuth `state` and redirects to it in
          // /auth/callback; without it every fresh login lands on '/'.
          const returnTo = window.location.pathname + window.location.search
          const loginUrl = `/auth/login?return_to=${encodeURIComponent(returnTo)}`
          try {
            const bypassRes = await fetch('/auth/bypass-check')
            const { bypass } = await bypassRes.json() as { bypass: boolean }
            if (bypass) {
              // Bypass active — /auth/login auto-sets the cookie and redirects to /.
              window.location.assign(loginUrl)
              return new Promise(() => {})
            }
          } catch {
            // bypass-check unavailable — fall through to normal login redirect.
          }
          window.location.assign(loginUrl)
          // Hang forever — the browser is unloading. Lets the React caller
          // stay in its loading state instead of catching ApiError and
          // flashing SignIn / LoginPage before the navigation completes.
          return new Promise(() => {})
        }
      }

      if (error.response.status === 403 && !error.config?.skipGlobal403) {
        // Authenticated but not authorized — could be Okta-group gap or
        // missing role permission. Show the existing UnauthorizedPage.
        // Callers that handle 403 themselves can pass skipGlobal403: true
        // (or use getFallible<T>) to suppress this navigation.
        window.location.href = '/unauthorized'
      }
    } else if (error.request) {
      apiError.message = 'Network error — please check your connection.'
    }

    return Promise.reject(apiError)
  }
)

// ─── Typed API helpers ─────────────────────────────────────────────────────────

export async function get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const response = await apiClient.get<T>(url, { params })
  return response.data
}

/** Like `get`, but a 403 response rejects normally instead of hard-navigating
 *  to /unauthorized. Use when the caller handles 403 itself (e.g. admin-only
 *  endpoints that non-admin users may legitimately hit). */
export async function getFallible<T>(url: string, params?: Record<string, unknown>): Promise<T> {
  const response = await apiClient.get<T>(url, { params, skipGlobal403: true })
  return response.data
}

export async function post<T>(url: string, data?: unknown): Promise<T> {
  const response = await apiClient.post<T>(url, data)
  return response.data
}

/**
 * Same as `post` but with a 180 s per-request timeout, for endpoints that
 * fan out to a long-running upstream (e.g. Anthropic Skills API calls,
 * which the backend itself caps at 120 s — this leaves a 60 s headroom).
 *
 * Use this only on call sites that genuinely need it.  The default 30 s
 * cap on `apiClient` stays appropriate for normal CRUD; widening it
 * globally would mask broken backends behind long-hanging spinners.
 */
export async function postLong<T>(url: string, data?: unknown): Promise<T> {
  const response = await apiClient.post<T>(url, data, { timeout: 180_000 })
  return response.data
}

export async function put<T>(url: string, data?: unknown): Promise<T> {
  const response = await apiClient.put<T>(url, data)
  return response.data
}

export async function patch<T>(url: string, data?: unknown): Promise<T> {
  const response = await apiClient.patch<T>(url, data)
  return response.data
}

export async function del<T>(url: string): Promise<T> {
  const response = await apiClient.delete<T>(url)
  return response.data
}

export async function postFormData<T>(url: string, formData: FormData): Promise<T> {
  const response = await apiClient.post<T>(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 120_000,
  })
  return response.data
}
